import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { X, Loader, KanbanSquare, AlertCircle, CheckCircle, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

import { eventTeamApiService } from '../../services/eventTeamApi';
import { kanbanApiService } from '../../services/kanbanApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  kanbanTeamsFromPermissions,
  kanbanTeamsFromStaffTeams,
  mergeKanbanTeamLists,
} from '../../utils/kanbanPermissions';
import { teamStaffOnlyKanbanWrite } from '../../utils/tesseractTeamAccess';
import { cn } from '../../utils/cn';
import type { KanbanBoard, KanbanList } from '../../types/kanban';
import type { EventKanbanLink, PromoteEventToCardRequest } from '../../types/eventTeam';

const CARD_TYPES = ['task', 'bug', 'feature', 'story', 'improvement', 'epic', 'subtask', 'other'];

type SyncFlagName =
  | 'sync_status'
  | 'sync_priority'
  | 'sync_due_date'
  | 'sync_assignees'
  | 'sync_comments'
  | 'propagate_watchers';

const SYNC_FLAG_LABELS: Record<SyncFlagName, string> = {
  sync_status: 'Status',
  sync_priority: 'Priority',
  sync_due_date: 'Due date',
  sync_assignees: 'Assignees',
  sync_comments: 'Comments',
  propagate_watchers: 'Watchers',
};

export interface PromoteEventToCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  /** Called after a successful promote (incl. the idempotent 200 `created_card:false` — guide §7.2). */
  onSuccess: (link: EventKanbanLink, cardId: string, createdCard: boolean) => void;
}

/**
 * Promote an event into a kanban card (guide §1.5) — idempotent per (event, board):
 * a second promote returns the existing card with `created_card:false`; treated as success.
 */
export const PromoteEventToCardModal = ({ isOpen, onClose, eventId, onSuccess }: PromoteEventToCardModalProps) => {
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  const writableTeams = useMemo(() => {
    if (!identity && !isDev) return [];
    const permissions = Array.isArray(identity?.raw?.custom?.permissions)
      ? (identity!.raw.custom.permissions as string[])
      : [];
    const groups = Array.isArray(identity?.raw?.custom?.groups)
      ? (identity!.raw.custom.groups as string[])
      : [];
    const isSuper = isDev || groups.includes('SPY_Super_admin') || permissions.includes('argus.kanban.super');
    const fromPermissions = kanbanTeamsFromPermissions(permissions);
    const fromStaff = kanbanTeamsFromStaffTeams(identity?.raw?.custom?.staff_teams);
    const all = mergeKanbanTeamLists(fromPermissions, fromStaff);
    if (isSuper) return all.length > 0 ? all : ['devteam', 'buildteam', 'eventteam', 'management'];
    return all.filter(
      (t) =>
        permissions.includes(`argus.kanban.write.${t}`) ||
        permissions.includes(`argus.kanban.manage.${t}`) ||
        teamStaffOnlyKanbanWrite(identity, isDev, t),
    );
  }, [identity, isDev]);

  const [team, setTeam] = useState('');
  const [useDefaultBoard, setUseDefaultBoard] = useState(true);
  const [boardId, setBoardId] = useState('');
  const [listId, setListId] = useState('');
  const [cardType, setCardType] = useState('task');
  const [copyAssignees, setCopyAssignees] = useState(true);
  const [notes, setNotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [syncFlags, setSyncFlags] = useState<Record<SyncFlagName, boolean>>({
    sync_status: true,
    sync_priority: true,
    sync_due_date: true,
    sync_assignees: true,
    sync_comments: true,
    propagate_watchers: true,
  });

  const [boards, setBoards] = useState<KanbanBoard[] | null>(null);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [lists, setLists] = useState<KanbanList[] | null>(null);
  const [listsLoading, setListsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    link: EventKanbanLink;
    cardId: string;
    boardId: string;
    createdCard: boolean;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSuccess(null);
      setError(null);
      return;
    }
    // Prefer eventteam when writable, otherwise the first writable team.
    if (!team && writableTeams.length > 0) {
      setTeam(writableTeams.includes('eventteam') ? 'eventteam' : writableTeams[0]!);
    }
  }, [isOpen, writableTeams, team]);

  useEffect(() => {
    if (!isOpen || !team || useDefaultBoard) {
      setBoards(null);
      return;
    }
    let cancelled = false;
    setBoardsLoading(true);
    kanbanApiService
      .listBoards({ team, limit: 100 })
      .then((res) => {
        if (!cancelled) setBoards(Array.isArray(res.results) ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setBoards([]);
      })
      .finally(() => {
        if (!cancelled) setBoardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, team, useDefaultBoard]);

  useEffect(() => {
    if (!isOpen || !boardId || useDefaultBoard) {
      setLists(null);
      return;
    }
    let cancelled = false;
    setListsLoading(true);
    kanbanApiService
      .listLists(boardId)
      .then((res) => {
        if (!cancelled) setLists(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (!cancelled) setLists([]);
      })
      .finally(() => {
        if (!cancelled) setListsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, boardId, useDefaultBoard]);

  const handleSubmit = async () => {
    if (!team) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: PromoteEventToCardRequest = {
        team,
        card_type: cardType,
        copy_assignees: copyAssignees,
        notes: notes.trim() || null,
        ...syncFlags,
      };
      if (!useDefaultBoard && boardId) {
        body.board_id = boardId;
        if (listId) body.list_id = listId;
      }
      const res = await eventTeamApiService.promoteToCard(eventId, body);
      setSuccess({
        link: res.link,
        cardId: res.card?.id ?? res.link.card_id,
        boardId: res.link.board_id,
        createdCard: res.created_card,
      });
      onSuccess(res.link, res.card?.id ?? res.link.card_id, res.created_card);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to promote event');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div className="modal-panel max-w-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-1 flex items-center gap-2">
          <KanbanSquare className="w-6 h-6 text-accent-400" />
          Promote to workspace card
        </h2>
        {success ? (
          <div className="space-y-4">
            <div
              className={cn(
                'flex items-start gap-2 p-3 rounded-lg border',
                success.createdCard
                  ? 'bg-green-500/10 border-green-500/20'
                  : 'bg-amber-500/10 border-amber-500/20',
              )}
            >
              <CheckCircle className={cn('w-5 h-5 flex-shrink-0', success.createdCard ? 'text-green-400' : 'text-amber-400')} />
              <div>
                <p className={cn('text-sm font-medium', success.createdCard ? 'text-green-300' : 'text-amber-300')}>
                  {success.createdCard ? 'Card created.' : 'A card already exists on that board. Reusing it.'}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="glass-button-secondary text-sm">
                Close
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate(`/workspaces/${encodeURIComponent(success.boardId)}?card=${encodeURIComponent(success.cardId)}`);
                }}
                className="glass-button text-sm inline-flex items-center gap-1.5"
              >
                <ExternalLink className="w-4 h-4" />
                Open card
              </button>
            </div>
          </div>
        ) : writableTeams.length === 0 ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm text-amber-300">
              You don't have kanban write access to any team (need{' '}
              <code>argus.kanban.write.eventteam</code>).
            </span>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Team</label>
                <select
                  value={team}
                  onChange={(e) => {
                    setTeam(e.target.value);
                    setBoardId('');
                    setListId('');
                  }}
                  className="glass-input w-full text-sm"
                >
                  {writableTeams.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Card type</label>
                <select value={cardType} onChange={(e) => setCardType(e.target.value)} className="glass-input w-full text-sm capitalize">
                  {CARD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer">
              <input
                type="checkbox"
                checked={useDefaultBoard}
                onChange={(e) => {
                  setUseDefaultBoard(e.target.checked);
                  setBoardId('');
                  setListId('');
                }}
                className="rounded border-glass-200/30"
              />
              Use the team's default board & list
            </label>

            {!useDefaultBoard && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-glass-300 mb-1 block">Board</label>
                  {boardsLoading ? (
                    <Loader className="w-4 h-4 animate-spin text-glass-400" />
                  ) : (
                    <select
                      value={boardId}
                      onChange={(e) => {
                        setBoardId(e.target.value);
                        setListId('');
                      }}
                      className="glass-input w-full text-sm"
                    >
                      <option value="">Select board…</option>
                      {(boards ?? []).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-sm text-glass-300 mb-1 block">List</label>
                  {listsLoading ? (
                    <Loader className="w-4 h-4 animate-spin text-glass-400" />
                  ) : (
                    <select
                      value={listId}
                      onChange={(e) => setListId(e.target.value)}
                      className="glass-input w-full text-sm"
                      disabled={!boardId}
                    >
                      <option value="">Default list</option>
                      {(lists ?? []).map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer">
              <input
                type="checkbox"
                checked={copyAssignees}
                onChange={(e) => setCopyAssignees(e.target.checked)}
                className="rounded border-glass-200/30"
              />
              Copy current assignees onto the card
            </label>

            <div>
              <label className="text-sm text-glass-300 mb-1 block">Bridge notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional context for this bridge…"
                className="glass-input w-full text-sm"
              />
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-glass-400 hover:text-glass-200 inline-flex items-center gap-1 transition-colors"
              >
                {showAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Sync flags ({Object.values(syncFlags).filter(Boolean).length}/6 on)
              </button>
              {showAdvanced && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(Object.keys(SYNC_FLAG_LABELS) as SyncFlagName[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSyncFlags((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all',
                        syncFlags[key]
                          ? 'bg-accent-500/20 border-accent-500/40 text-accent-200'
                          : 'bg-glass-100/5 border-glass-200/20 text-glass-400 hover:text-white',
                      )}
                    >
                      {SYNC_FLAG_LABELS[key]}
                      <span className="font-mono text-[9px] opacity-70">{syncFlags[key] ? 'on' : 'off'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="glass-button-secondary text-sm">
                Cancel
              </button>
              <button
                onClick={() => void handleSubmit()}
                disabled={submitting || !team || (!useDefaultBoard && !boardId)}
                className="glass-button text-sm inline-flex items-center gap-1.5"
              >
                {submitting && <Loader className="w-4 h-4 animate-spin" />}
                Promote
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
